import { useState, useRef, useEffect } from "react";
import { db } from "./firebase";
import { collection, addDoc } from "firebase/firestore";

export default function CreateTab({ user }) {
  const uid = user.uid;
  const [activeTool, setActiveTool] = useState(null);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifySubmitted, setNotifySubmitted] = useState(false);

if (activeTool === "studio") return <SongStudio uid={uid} user={user} onBack={() => setActiveTool(null)} />;
  if (activeTool === "beatmaker") return <BeatMaker onBack={() => setActiveTool(null)} />;
  if (activeTool === "piano") return <VirtualPiano onBack={() => setActiveTool(null)} />;
  if (activeTool === "recorder") return <VoiceRecorder onBack={() => setActiveTool(null)} />;
  if (activeTool === "lyrics") return <LyricsWriter uid={uid} onBack={() => setActiveTool(null)} />;
  if (activeTool === "guitar") return <VirtualGuitar onBack={() => setActiveTool(null)} />;

  return (
    <div style={{ minHeight: "100vh", background: "#000", color: "#fff", fontFamily: "'DM Sans', sans-serif", paddingBottom: 80 }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Syne:wght@700;800&family=DM+Sans:wght@400;500;600&display=swap'); @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} } @keyframes pulse { 0%,100%{opacity:0.4} 50%{opacity:1} } @keyframes spin { to{transform:rotate(360deg)} }`}</style>

      {/* Header */}
      <div style={{ padding: "20px 20px 16px", borderBottom: "1px solid #0a0a0a" }}>
        <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 24, fontWeight: 800, marginBottom: 4 }}>Create</div>
        <div style={{ fontSize: 13, color: "#555" }}>Make your own music</div>
      </div>

      {/* Song Studio — main feature */}
      <div style={{ padding: "16px 16px 0" }}>
        <div onClick={() => setActiveTool("studio")} style={{ background: "linear-gradient(135deg, #0d2818, #1a4a2a)", borderRadius: 16, padding: 20, border: "1px solid #1db954", cursor: "pointer", marginBottom: 16, position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: -20, right: -20, width: 100, height: 100, borderRadius: "50%", background: "rgba(29,185,84,0.1)" }} />
          <div style={{ fontSize: 10, color: "#1db954", fontWeight: 700, letterSpacing: "0.15em", marginBottom: 8 }}>NEW · BETA</div>
          <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 22, fontWeight: 800, marginBottom: 6 }}>🎛️ Song Studio</div>
          <div style={{ fontSize: 13, color: "#888", marginBottom: 16 }}>Combine Beat + Piano + Voice + Lyrics into one song</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {["🥁 Beat", "🎹 Piano", "🎙️ Voice", "✍️ Lyrics"].map(f => (
              <div key={f} style={{ padding: "4px 12px", borderRadius: 50, background: "rgba(29,185,84,0.15)", border: "1px solid rgba(29,185,84,0.3)", fontSize: 12, color: "#1db954" }}>{f}</div>
            ))}
          </div>
        </div>

        {/* Individual tools */}
        <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 14, fontWeight: 700, color: "#555", marginBottom: 12, letterSpacing: "0.05em" }}>INDIVIDUAL TOOLS</div>
        {[
          { id: "beatmaker", icon: "🥁", name: "Beat Maker", desc: "Create drum patterns", color: "#1db954" },
          { id: "piano", icon: "🎹", name: "Virtual Piano", desc: "Play and record melodies", color: "#4776e6" },
          { id: "recorder", icon: "🎙️", name: "Voice Recorder", desc: "Record your voice", color: "#e91429" },
          { id: "lyrics", icon: "✍️", name: "Lyrics Writer", desc: "Write your song lyrics", color: "#8b5cf6" },
          { id: "guitar", icon: "🎸", name: "Virtual Guitar", desc: "Strum chords and record", color: "#f59e0b" },
        ].map(tool => (
          <div key={tool.id} onClick={() => setActiveTool(tool.id)} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", background: "#111", borderRadius: 12, marginBottom: 10, cursor: "pointer", border: "1px solid #1a1a1a" }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: `${tool.color}22`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>{tool.icon}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: tool.color }}>{tool.name}</div>
              <div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>{tool.desc}</div>
            </div>
            <svg viewBox="0 0 24 24" style={{ width: 16, height: 16, fill: "none", stroke: "#333", strokeWidth: 2 }}><polyline points="9 18 15 12 9 6"/></svg>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── SONG STUDIO ──────────────────────────────────────────────────────────────
function SongStudio({ uid, user, onBack }) {
  const [savedBeats] = useState(() => JSON.parse(localStorage.getItem("saved_beats") || "[]"));
  const [savedMelodies] = useState(() => JSON.parse(localStorage.getItem("saved_melodies") || "[]"));
  const [savedRecordings] = useState(() => JSON.parse(localStorage.getItem(`saved_recordings_${uid}`) || "[]"));
  const [savedLyrics] = useState(() => { try { return JSON.parse(localStorage.getItem(`my_lyrics_${uid}`) || "[]"); } catch { return []; } });

  const [selectedBeat, setSelectedBeat] = useState(null);
  const [selectedMelody, setSelectedMelody] = useState(null);
  const [selectedRecording, setSelectedRecording] = useState(null);
  const [selectedGuitar, setSelectedGuitar] = useState(null);
  const [selectedLyrics, setSelectedLyrics] = useState(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [mixVolumes, setMixVolumes] = useState({ beatVolume: 1, pianoVolume: 1, guitarVolume: 1, voiceVolume: 1 });
  const [syncLyrics, setSyncLyrics] = useState("");
const [syncLines, setSyncLines] = useState([]);
const [syncTimed, setSyncTimed] = useState([]);
const [syncIndex, setSyncIndex] = useState(0);
const [syncStarted, setSyncStarted] = useState(false);
const [syncStartTime, setSyncStartTime] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  // Volume controls per track
const [beatVolume, setBeatVolume] = useState(80);
const [melodyVolume, setMelodyVolume] = useState(70);
const [voiceVolume, setVoiceVolume] = useState(90);

// Mute controls per track
const [beatMuted, setBeatMuted] = useState(false);
const [melodyMuted, setMelodyMuted] = useState(false);
const [voiceMuted, setVoiceMuted] = useState(false);
const [guitarVolume, setGuitarVolume] = useState(80);
const [guitarMuted, setGuitarMuted] = useState(false);
const guitarTimeoutsRef = useRef([]);

  const intervalRef = useRef(null);
  const audioCtxRef = useRef(null);
  const stepRef = useRef(0);
  const melodyTimeoutsRef = useRef([]);
  const audioRef = useRef(null);

  function getCtx() {
    if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    return audioCtxRef.current;
  }

  function playBeatSound(type) {
    if (beatMuted) return;
    const ctx = getCtx();
    const gain = ctx.createGain();
    gain.gain.value = beatVolume / 100;
    gain.connect(ctx.destination);
    if (type === "kick") {
      const osc = ctx.createOscillator();
      osc.connect(gain);
      osc.frequency.setValueAtTime(150, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      gain.gain.setValueAtTime(1, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      osc.type = "sine"; osc.start(); osc.stop(ctx.currentTime + 0.5);
    } else if (type === "snare" || type === "clap" || type === "hihat") {
      const buf = ctx.createBuffer(1, ctx.sampleRate * (type === "hihat" ? 0.05 : 0.2), ctx.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
      const noise = ctx.createBufferSource();
      noise.buffer = buf;
      if (type === "hihat") {
        const filter = ctx.createBiquadFilter();
        filter.type = "highpass"; filter.frequency.value = 8000;
        noise.connect(filter); filter.connect(gain);
      } else { noise.connect(gain); }
      gain.gain.setValueAtTime(type === "hihat" ? 0.3 : 0.7, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
      noise.start(); noise.stop(ctx.currentTime + 0.2);
    } else {
      const osc = ctx.createOscillator();
      osc.connect(gain);
      osc.frequency.setValueAtTime(type === "bass" ? 80 : 200, ctx.currentTime);
      gain.gain.setValueAtTime(0.8, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
      osc.type = type === "bass" ? "sawtooth" : "sine"; osc.start(); osc.stop(ctx.currentTime + 0.3);
    }
  }

 function playPianoNote(note, oct) {
    if (melodyMuted) return;
    const ctx = getCtx();
    const notes = { C: 0, "C#": 1, D: 2, "D#": 3, E: 4, F: 5, "F#": 6, G: 7, "G#": 8, A: 9, "A#": 10, B: 11 };
    const freq = 440 * Math.pow(2, (notes[note] + (oct + 1) * 12 - 69) / 12);
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.type = "triangle"; osc.frequency.value = freq;
gain.gain.setValueAtTime(melodyVolume / 100 * 0.5, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 1.5);
    osc.start(); osc.stop(ctx.currentTime + 1.5);
  }

  function playAll() {
    if (isPlaying) { stopAll(); return; }
    setIsPlaying(true);

    // Play beat
    if (selectedBeat) {
      const bpm = selectedBeat.bpm || 120;
      const pattern = selectedBeat.pattern;
      stepRef.current = 0;
      intervalRef.current = setInterval(() => {
        const step = stepRef.current;
        setCurrentStep(step);
        if (pattern) {
          Object.keys(pattern).forEach(track => { if (pattern[track][step]) playBeatSound(track); });
        }
        stepRef.current = (step + 1) % 16;
      }, (60 / bpm / 4) * 1000);
    }

    // Play guitar
if (selectedGuitar && !guitarMuted) {
  guitarTimeoutsRef.current.forEach(t => clearTimeout(t));
  const guitarChords = [
    { name: "C", freqs: [261.63, 329.63, 392.00, 523.25, 659.25] },
    { name: "G", freqs: [196.00, 246.94, 293.66, 392.00, 493.88] },
    { name: "Am", freqs: [220.00, 261.63, 329.63, 440.00, 523.25] },
    { name: "F", freqs: [174.61, 220.00, 261.63, 349.23, 440.00] },
    { name: "D", freqs: [146.83, 185.00, 220.00, 293.66, 369.99] },
    { name: "Em", freqs: [164.81, 196.00, 246.94, 329.63, 392.00] },
    { name: "A", freqs: [110.00, 138.59, 164.81, 220.00, 277.18] },
    { name: "E", freqs: [82.41, 110.00, 164.81, 246.94, 329.63] },
    { name: "Dm", freqs: [146.83, 174.61, 220.00, 293.66, 349.23] },
    { name: "Bm", freqs: [123.47, 146.83, 185.00, 246.94, 293.66] },
    { name: "C7", freqs: [261.63, 311.13, 392.00, 466.16, 523.25] },
    { name: "G7", freqs: [196.00, 246.94, 293.66, 349.23, 440.00] },
  ];
  guitarTimeoutsRef.current = selectedGuitar.chords.map(({ chord, time }) =>
    setTimeout(() => {
      const ctx = getCtx();
      const c = guitarChords.find(g => g.name === chord);
      if (!c) return;
      c.freqs.forEach((freq, i) => {
        setTimeout(() => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          gain.gain.value = guitarVolume / 100 * 0.3;
          gain.connect(ctx.destination);
          osc.connect(gain);
          osc.type = "sawtooth";
          osc.frequency.value = freq;
          gain.gain.setValueAtTime(guitarVolume / 100 * 0.3, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.8);
          osc.start();
          osc.stop(ctx.currentTime + 0.8);
        }, i * 30);
      });
    }, time)
  );
}
    // Play melody
    if (selectedMelody) {
      melodyTimeoutsRef.current.forEach(t => clearTimeout(t));
      guitarTimeoutsRef.current.forEach(t => clearTimeout(t));
      melodyTimeoutsRef.current = selectedMelody.notes.map(({ note, oct, time }) =>
        setTimeout(() => playPianoNote(note, oct), time)
      );
    }

    // Play recording
   if (selectedRecording && audioRef.current && !voiceMuted) {
      audioRef.current.volume = voiceVolume / 100;
      audioRef.current.play();
    }
  }

  function stopAll() {
    clearInterval(intervalRef.current);
    melodyTimeoutsRef.current.forEach(t => clearTimeout(t));
    guitarTimeoutsRef.current.forEach(t => clearTimeout(t));
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
    setIsPlaying(false);
    setCurrentStep(0);
    stepRef.current = 0;
  }

  useEffect(() => { return () => stopAll(); }, []);

  return (
   <div style={{ background: "#0a0a0a", minHeight: "100vh", paddingBottom: 200, color: "#fff", fontFamily: "'DM Sans',sans-serif" }}>
  <style>{`@import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&display=swap'); @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} } @keyframes spin { to{transform:rotate(360deg)} }`}</style>
  <BackButton onBack={() => { stopAll(); onBack(); }} title="🎛️ Song Studio" />

      <div style={{ padding: 16 }}>
        {/* Header with Play Button */}
<div style={{ background: "linear-gradient(180deg, #111 0%, #0a0a0a 100%)", borderRadius: 20, padding: 20, marginBottom: 16, border: "1px solid #1a1a1a", textAlign: "center" }}>
  <div style={{ marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
    {[
      { track: selectedBeat, color: "#1db954", label: "Beat" },
      { track: selectedMelody, color: "#a78bfa", label: "Piano" },
      { track: selectedGuitar, color: "#f59e0b", label: "Guitar" },
      { track: selectedRecording, color: "#f87171", label: "Voice" },
    ].map(({ track, color, label }) => (
      <div key={label} style={{
        padding: "3px 8px", borderRadius: 20, fontSize: 10, fontWeight: 700,
        background: track ? `${color}22` : "#1a1a1a",
        color: track ? color : "#333",
        border: `1px solid ${track ? color : "#222"}`,
        transition: "all 0.2s"
      }}>{label}</div>
    ))}
  </div>
  <button onClick={playAll} style={{ width: 72, height: 72, borderRadius: "50%", border: `3px solid ${isPlaying ? "#e91429" : "#1db954"}`, background: isPlaying ? "rgba(233,20,41,0.1)" : "rgba(29,185,84,0.1)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px", transition: "all 0.2s" }}>
    {isPlaying ? (
      <svg viewBox="0 0 24 24" style={{ width: 28, height: 28, fill: "#e91429" }}><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
    ) : (
      <svg viewBox="0 0 24 24" style={{ width: 28, height: 28, fill: "#1db954", marginLeft: 4 }}><polygon points="5 3 19 12 5 21 5 3"/></svg>
    )}
  </button>
  <div style={{ fontSize: 14, fontWeight: 600, color: isPlaying ? "#e91429" : "#fff", marginBottom: 4 }}>
    {isPlaying ? "Now Playing" : "Play All Tracks"}
  </div>
  <div style={{ fontSize: 12, color: "#555" }}>
    {isPlaying ? "Tap to stop" : selectedBeat || selectedMelody || selectedRecording ? "Ready to play" : "Add tracks below to start"}
  </div>
  {/* Beat step indicator */}
  {isPlaying && selectedBeat && (
    <div style={{ display: "flex", gap: 3, marginTop: 14 }}>
      {Array(16).fill(0).map((_, i) => (
        <div key={i} style={{ flex: 1, height: 4, borderRadius: 2, background: i === currentStep ? "#1db954" : "#1a1a1a", transition: "background 0.05s" }} />
      ))}
    </div>
  )}
</div>

        {/* Beat Track */}
        <TrackSelector
          icon="🥁"
          label="Beat Track"
          color="#1db954"
          items={savedBeats}
          selected={selectedBeat}
          onSelect={setSelectedBeat}
          emptyMsg="No saved beats. Go to Beat Maker and save one."
          renderItem={b => `${b.name} · ${b.bpm} BPM`}
          isPlaying={isPlaying}
        />

        {/* Piano Track */}
        <TrackSelector
          icon="🎹"
          label="Piano Track"
          color="#4776e6"
          items={savedMelodies}
          selected={selectedMelody}
          onSelect={setSelectedMelody}
          emptyMsg="No saved melodies. Go to Piano and record one."
          renderItem={m => `${m.name} · ${m.notes?.length || 0} notes`}
          isPlaying={isPlaying}
        />

       {/* Guitar Track */}
<TrackSelector
  icon="🎸"
  label="Guitar Track"
  color="#f59e0b"
  items={JSON.parse(localStorage.getItem("saved_guitar") || "[]")}
  selected={selectedGuitar}
  onSelect={setSelectedGuitar}
  emptyMsg="No saved guitar recordings. Go to Virtual Guitar and save one."
  renderItem={g => `${g.name} · ${g.chords.length} chords`}
  isPlaying={isPlaying}
/>
        {/* Voice Track */}
        <TrackSelector
          icon="🎙️"
          label="Voice Track"
          color="#e91429"
          items={savedRecordings}
          selected={selectedRecording}
          onSelect={setSelectedRecording}
          emptyMsg="No saved recordings. Go to Voice Recorder and save one."
          renderItem={r => r.name}
          isPlaying={isPlaying}
        />

        {/* Hidden audio for voice playback */}
        {selectedRecording && (
          <audio ref={audioRef} src={selectedRecording.url} loop={false} />
        )}

        {/* Lyrics Track */}
        <div style={{ background: "#111", borderRadius: 14, padding: 16, border: "1px solid #1a1a1a", marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: "#8b5cf622", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>✍️</div>
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 14, fontWeight: 800 }}>Lyrics</div>
          </div>
          {savedLyrics.length === 0 ? (
            <div style={{ fontSize: 12, color: "#444", textAlign: "center", padding: "8px 0" }}>No saved lyrics. Go to Lyrics Writer and save some.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {savedLyrics.map(l => (
                <div key={l.id} onClick={() => setSelectedLyrics(selectedLyrics?.id === l.id ? null : l)}
                  style={{ padding: "10px 14px", borderRadius: 10, border: `1px solid ${selectedLyrics?.id === l.id ? "#8b5cf6" : "#2a2a2a"}`, background: selectedLyrics?.id === l.id ? "rgba(139,92,246,0.1)" : "#0a0a0a", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 13, color: selectedLyrics?.id === l.id ? "#8b5cf6" : "#ccc" }}>{l.title}</span>
                  {selectedLyrics?.id === l.id && <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "none", stroke: "#8b5cf6", strokeWidth: 2.5 }}><polyline points="20 6 9 17 4 12"/></svg>}
                </div>
              ))}
            </div>
          )}
          {selectedLyrics && (
            <div style={{ marginTop: 12, background: "#0a0a0a", borderRadius: 10, padding: 14, maxHeight: 200, overflowY: "auto" }}>
              <pre style={{ fontSize: 13, color: "#888", lineHeight: 1.8, whiteSpace: "pre-wrap", fontFamily: "'DM Sans',sans-serif" }}>{selectedLyrics.content}</pre>
            </div>
          )}
        </div>

        {/* Mixer Controls */}
{(selectedBeat || selectedMelody || selectedRecording || selectedGuitar) && (
  <div style={{ background: "#111", borderRadius: 14, padding: 16, border: "1px solid #1a1a1a", marginBottom: 12 }}>
    <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 14, fontWeight: 800, marginBottom: 14 }}>🎛️ Mixer</div>
    
    {selectedBeat && (
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <span style={{ fontSize: 16 }}>🥁</span>
          <span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>Beat</span>
          <button onClick={() => setBeatMuted(!beatMuted)} style={{ padding: "4px 10px", borderRadius: 6, border: "none", background: beatMuted ? "#e91429" : "#2a2a2a", color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
            {beatMuted ? "MUTED" : "MUTE"}
          </button>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 11, color: "#555", minWidth: 20 }}>🔈</span>
          <input type="range" min="0" max="100" value={beatVolume} onChange={e => setBeatVolume(Number(e.target.value))} style={{ flex: 1, accentColor: "#1db954" }} />
          <span style={{ fontSize: 11, color: "#1db954", minWidth: 30 }}>{beatVolume}%</span>
        </div>
      </div>
    )}

    {selectedMelody && (
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <span style={{ fontSize: 16 }}>🎹</span>
          <span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>Piano</span>
          <button onClick={() => setMelodyMuted(!melodyMuted)} style={{ padding: "4px 10px", borderRadius: 6, border: "none", background: melodyMuted ? "#e91429" : "#2a2a2a", color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
            {melodyMuted ? "MUTED" : "MUTE"}
          </button>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 11, color: "#555", minWidth: 20 }}>🔈</span>
          <input type="range" min="0" max="100" value={melodyVolume} onChange={e => setMelodyVolume(Number(e.target.value))} style={{ flex: 1, accentColor: "#4776e6" }} />
          <span style={{ fontSize: 11, color: "#4776e6", minWidth: 30 }}>{melodyVolume}%</span>
        </div>
      </div>
    )}

    {selectedGuitar && (
  <div style={{ marginBottom: 14 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
      <span style={{ fontSize: 16 }}>🎸</span>
      <span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>Guitar</span>
      <button onClick={() => setGuitarMuted(!guitarMuted)} style={{ padding: "4px 10px", borderRadius: 6, border: "none", background: guitarMuted ? "#e91429" : "#2a2a2a", color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
        {guitarMuted ? "MUTED" : "MUTE"}
      </button>
    </div>
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <span style={{ fontSize: 11, color: "#555", minWidth: 20 }}>🔈</span>
      <input type="range" min="0" max="100" value={guitarVolume} onChange={e => setGuitarVolume(Number(e.target.value))} style={{ flex: 1, accentColor: "#f59e0b" }} />
      <span style={{ fontSize: 11, color: "#f59e0b", minWidth: 30 }}>{guitarVolume}%</span>
    </div>
  </div>
)}
    {selectedRecording && (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <span style={{ fontSize: 16 }}>🎙️</span>
          <span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>Voice</span>
          <button onClick={() => setVoiceMuted(!voiceMuted)} style={{ padding: "4px 10px", borderRadius: 6, border: "none", background: voiceMuted ? "#e91429" : "#2a2a2a", color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
            {voiceMuted ? "MUTED" : "MUTE"}
          </button>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 11, color: "#555", minWidth: 20 }}>🔈</span>
          <input type="range" min="0" max="100" value={voiceVolume} onChange={e => { setVoiceVolume(Number(e.target.value)); if (audioRef.current) audioRef.current.volume = Number(e.target.value) / 100; }} style={{ flex: 1, accentColor: "#e91429" }} />
          <span style={{ fontSize: 11, color: "#e91429", minWidth: 30 }}>{voiceVolume}%</span>
        </div>
      </div>
    )}
  </div>
)}
        {/* Beat visualizer when playing */}
        {isPlaying && selectedBeat && (
          <div style={{ background: "#111", borderRadius: 14, padding: 16, border: "1px solid #1a1a1a", marginTop: 8 }}>
            <div style={{ fontSize: 12, color: "#555", marginBottom: 10 }}>Now playing</div>
            <div style={{ display: "flex", gap: 3 }}>
              {Array(16).fill(0).map((_, i) => (
                <div key={i} style={{ flex: 1, height: 4, borderRadius: 2, background: i === currentStep ? "#1db954" : "#2a2a2a", transition: "background 0.1s" }} />
              ))}
            </div>
          </div>
        )}
      </div>
    {/* Lyrics Sync */}
<div style={{ background: "#111", borderRadius: 14, padding: 16, border: "1px solid #333", marginTop: 12 }}>
  <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 14, fontWeight: 800, marginBottom: 2 }}>🎵 Lyrics Sync</div>
  <div style={{ fontSize: 12, color: "#555", marginBottom: 14 }}>Tap each line in time with your beat</div>

  {!syncStarted ? (
    <div>
      <textarea
        value={syncLyrics}
        onChange={e => setSyncLyrics(e.target.value)}
        placeholder={"Enter your lyrics here...\nOne line per beat\nTap the button to sync timing"}
        style={{
          width: "100%", background: "#0a0a0a", border: "1px solid #222", borderRadius: 10,
          color: "#fff", fontSize: 13, padding: "10px 12px", resize: "none", height: 100,
          boxSizing: "border-box", marginBottom: 10, fontFamily: "inherit"
        }}
      />
      <button onClick={() => {
        if (!syncLyrics.trim()) return;
        setSyncLines(syncLyrics.split("\n").filter(l => l.trim()));
        setSyncTimed([]);
        setSyncIndex(0);
        setSyncStarted(true);
        setSyncStartTime(Date.now());
      }} style={{
        width: "100%", padding: "11px", borderRadius: 10, border: "none",
        background: "#1db954", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer"
      }}>▶ Start Sync</button>
    </div>
  ) : (
    <div>
      <div style={{ fontSize: 12, color: "#555", marginBottom: 10 }}>
        Line {syncIndex + 1} of {syncLines.length} — tap the button when you sing each line
      </div>
      <div style={{
        background: "#0a0a0a", borderRadius: 10, padding: "14px 16px", marginBottom: 12,
        fontSize: 16, fontWeight: 700, color: "#1db954", textAlign: "center", minHeight: 48,
        display: "flex", alignItems: "center", justifyContent: "center"
      }}>
        {syncIndex < syncLines.length ? syncLines[syncIndex] : "✅ Done!"}
      </div>
      {syncIndex < syncLines.length ? (
        <button onClick={() => {
          const time = Date.now() - syncStartTime;
          setSyncTimed(t => [...t, { line: syncLines[syncIndex], time }]);
          setSyncIndex(i => i + 1);
        }} style={{
          width: "100%", padding: "14px", borderRadius: 10, border: "none",
          background: "linear-gradient(135deg, #a78bfa, #7c3aed)",
          color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", marginBottom: 8
        }}>👆 Tap — Next Line</button>
      ) : (
        <div>
          <div style={{ fontSize: 12, color: "#555", marginBottom: 10 }}>Timed lyrics saved! Added to your project.</div>
          <div style={{ maxHeight: 120, overflowY: "auto", marginBottom: 10 }}>
            {syncTimed.map((t, i) => (
              <div key={i} style={{ fontSize: 12, color: "#aaa", padding: "3px 0", borderBottom: "1px solid #1a1a1a" }}>
                <span style={{ color: "#555", marginRight: 8 }}>{(t.time / 1000).toFixed(2)}s</span>{t.line}
              </div>
            ))}
          </div>
          <button onClick={() => { setSyncStarted(false); setSyncLyrics(""); setSyncTimed([]); setSyncIndex(0); }}
            style={{ width: "100%", padding: "10px", borderRadius: 10, border: "1px solid #333", background: "#1a1a1a", color: "#fff", fontSize: 13, cursor: "pointer" }}>
            🔄 Reset
          </button>
        </div>
      )}
    </div>
  )}
</div>
      {/* Mix & Master Panel */}
<div style={{ background: "#111", borderRadius: 14, padding: 16, border: "1px solid #333", marginTop: 12 }}>
  <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 14, fontWeight: 800, marginBottom: 2 }}>🎚️ Mix & Master</div>
  <div style={{ fontSize: 12, color: "#555", marginBottom: 14 }}>Balance your tracks</div>

  {[
    { label: "🥁 Beat", key: "beatVolume", color: "#1db954" },
    { label: "🎹 Piano", key: "pianoVolume", color: "#a78bfa" },
    { label: "🎸 Guitar", key: "guitarVolume", color: "#f59e0b" },
    { label: "🎤 Voice", key: "voiceVolume", color: "#f87171" },
  ].map(({ label, key, color }) => (
    <div key={key} style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
        <span>{label}</span>
        <span style={{ color }}>{Math.round((mixVolumes[key] ?? 1) * 100)}%</span>
      </div>
      <input
        type="range"
        min="0"
        max="1"
        step="0.01"
        value={mixVolumes[key] ?? 1}
        onChange={e => setMixVolumes(v => ({ ...v, [key]: parseFloat(e.target.value) }))}
        style={{ width: "100%", accentColor: color, cursor: "pointer" }}
      />
    </div>
  ))}
</div>
      {/* Save & Share Project */}
<div style={{ background: "#111", borderRadius: 14, padding: 16, border: "1px solid #1db954", marginTop: 12, marginBottom: 80 }}>
  <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 14, fontWeight: 800, marginBottom: 4 }}>💾 Save Project</div>
  <div style={{ fontSize: 12, color: "#555", marginBottom: 12 }}>Save your song project to come back later</div>
 <SaveProjectButton
    uid={uid}
    selectedBeat={selectedBeat}
    selectedMelody={selectedMelody}
    selectedRecording={selectedRecording}
    selectedGuitar={selectedGuitar}
    guitarVolume={guitarVolume}
    selectedLyrics={selectedLyrics}
    beatVolume={beatVolume}
    melodyVolume={melodyVolume}
    voiceVolume={voiceVolume}
  />
  <ExportProjectButton
    selectedBeat={selectedBeat}
    selectedMelody={selectedMelody}
    selectedGuitar={selectedGuitar}
    selectedRecording={selectedRecording}
    selectedLyrics={selectedLyrics}
    mixVolumes={mixVolumes}
  />
</div>

{/* Share to Feed */}
<div style={{ background: "#111", borderRadius: 14, padding: 16, border: "1px solid #333", marginTop: 12, marginBottom: 80 }}>
  <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 14, fontWeight: 800, marginBottom: 2 }}>📢 Share to Feed</div>
  <div style={{ fontSize: 12, color: "#555", marginBottom: 14 }}>Post your Song Studio project to the community</div>
 <ShareToFeedButton
    uid={uid}
    user={user}
    selectedBeat={selectedBeat}
    selectedMelody={selectedMelody}
    selectedGuitar={selectedGuitar}
    selectedRecording={selectedRecording}
    selectedLyrics={selectedLyrics}
    mixVolumes={mixVolumes}
  />
</div>

      </div>
  );
}

function SaveProjectButton({ uid, selectedBeat, selectedMelody, selectedGuitar, selectedRecording, selectedLyrics, beatVolume, melodyVolume, guitarVolume, voiceVolume }) {
  const [projectName, setProjectName] = useState("");
  const [saved, setSaved] = useState(false);

  function saveProject() {
    if (!selectedBeat && !selectedMelody && !selectedGuitar && !selectedRecording) {
      alert("Add at least one track before saving!"); return;
    }
    const name = projectName.trim() || `Project ${new Date().toLocaleDateString()}`;
    const project = {
      id: Date.now(),
      name,
      beat: selectedBeat ? { id: selectedBeat.id, name: selectedBeat.name, bpm: selectedBeat.bpm } : null,
      melody: selectedMelody ? { id: selectedMelody.id, name: selectedMelody.name } : null,
      guitar: selectedGuitar ? { id: selectedGuitar.id, name: selectedGuitar.name, chords: selectedGuitar.chords.length } : null,
      recording: selectedRecording ? { id: selectedRecording.id, name: selectedRecording.name } : null,
      lyrics: selectedLyrics ? { id: selectedLyrics.id, title: selectedLyrics.title } : null,
      beatVolume, melodyVolume, voiceVolume,
      date: new Date().toLocaleDateString(),
      createdAt: Date.now(),
    };
    const existing = JSON.parse(localStorage.getItem(`projects_${uid}`) || "[]");
    const updated = [project, ...existing];
    localStorage.setItem(`projects_${uid}`, JSON.stringify(updated));
    setProjectName("");
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  }

  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
        <input value={projectName} onChange={e => setProjectName(e.target.value)} placeholder="Project name..."
          style={{ flex: 1, padding: "10px 13px", borderRadius: 10, border: "1px solid #2a2a2a", background: "#0a0a0a", color: "#fff", fontFamily: "'DM Sans',sans-serif", fontSize: 13, outline: "none" }} />
        <button onClick={saveProject} style={{ padding: "10px 20px", borderRadius: 10, border: "none", background: "#1db954", color: "#000", fontSize: 13, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>
          Save
        </button>
      </div>
      {saved && <div style={{ color: "#1db954", fontSize: 12, textAlign: "center" }}>✅ Project saved!</div>}
      <ProjectsList uid={uid} />
    </div>
  );
}

function ProjectsList({ uid }) {
  const [projects, setProjects] = useState(() => JSON.parse(localStorage.getItem(`projects_${uid}`) || "[]"));
  const [showAll, setShowAll] = useState(false);

  if (projects.length === 0) return null;

  const displayed = showAll ? projects : projects.slice(0, 3);

  function deleteProject(id) {
    const updated = projects.filter(p => p.id !== id);
    setProjects(updated);
    localStorage.setItem(`projects_${uid}`, JSON.stringify(updated));
  }

  function shareProject(project) {
    const text = `🎵 I made a song on MyMusic!\n\n"${project.name}"\n${project.beat ? `🥁 Beat: ${project.beat.name}` : ""}\n${project.melody ? `🎹 Piano: ${project.melody.name}` : ""}\n${project.lyrics ? `✍️ Lyrics: ${project.lyrics.title}` : ""}\n\nCreate your own → mymusicstream.online`;
    if (navigator.share) navigator.share({ title: project.name, text });
    else { navigator.clipboard.writeText(text); alert("Copied! Share your project 🎵"); }
  }

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 13, fontWeight: 800, marginBottom: 10, color: "#888" }}>MY PROJECTS ({projects.length})</div>
      {displayed.map((project, i) => (
        <div key={project.id} style={{ background: "#0a0a0a", borderRadius: 10, padding: 12, marginBottom: 8, border: "1px solid #1a1a1a" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: "linear-gradient(135deg, #1db954, #0a8f3c)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>🎛️</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{project.name}</div>
              <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>{project.date}</div>
            </div>
            <button onClick={() => shareProject(project)} style={{ padding: "5px 10px", borderRadius: 8, border: "none", background: "#1db954", color: "#000", fontSize: 11, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>Share</button>
            <button onClick={() => deleteProject(project.id)} style={{ background: "none", border: "none", color: "#444", cursor: "pointer", fontSize: 14 }}>🗑️</button>
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {project.beat && <span style={{ padding: "3px 8px", borderRadius: 50, background: "rgba(29,185,84,0.1)", border: "1px solid rgba(29,185,84,0.2)", fontSize: 11, color: "#1db954" }}>🥁 {project.beat.name}</span>}
            {project.melody && <span style={{ padding: "3px 8px", borderRadius: 50, background: "rgba(71,118,230,0.1)", border: "1px solid rgba(71,118,230,0.2)", fontSize: 11, color: "#4776e6" }}>🎹 {project.melody.name}</span>}
            {project.recording && <span style={{ padding: "3px 8px", borderRadius: 50, background: "rgba(233,20,41,0.1)", border: "1px solid rgba(233,20,41,0.2)", fontSize: 11, color: "#e91429" }}>🎙️ {project.recording.name}</span>}
            {project.lyrics && <span style={{ padding: "3px 8px", borderRadius: 50, background: "rgba(139,92,246,0.1)", border: "1px solid rgba(139,92,246,0.2)", fontSize: 11, color: "#8b5cf6" }}>✍️ {project.lyrics.title}</span>}
          </div>
        </div>
      ))}
      {projects.length > 3 && (
        <button onClick={() => setShowAll(!showAll)} style={{ width: "100%", padding: "8px 0", borderRadius: 10, border: "1px solid #2a2a2a", background: "transparent", color: "#555", fontSize: 12, cursor: "pointer" }}>
          {showAll ? "Show less" : `Show ${projects.length - 3} more`}
        </button>
      )}
    </div>
  );
}
function ShareToFeedButton({ uid, user, selectedBeat, selectedMelody, selectedGuitar, selectedRecording, selectedLyrics, mixVolumes }) {
  const [caption, setCaption] = useState("");
  const [sharing, setSharing] = useState(false);
  const [shared, setShared] = useState(false);

  const handleShare = async () => {
    if (!uid) return;
    setSharing(true);
    try {
      const tracks = [];
      if (selectedBeat) tracks.push(`🥁 ${selectedBeat.name || "Beat"}`);
      if (selectedMelody) tracks.push(`🎹 ${selectedMelody.name || "Piano"}`);
      if (selectedGuitar) tracks.push(`🎸 ${selectedGuitar.name || "Guitar"}`);
      if (selectedRecording) tracks.push(`🎤 ${selectedRecording.name || "Voice"}`);

      if (tracks.length === 0) {
        alert("Add at least one track before sharing.");
        setSharing(false);
        return;
      }

     await addDoc(collection(db, "songs"), {
        uid,
        artistId: uid,
        artistName: user?.displayName || "Artist",
        artistPhoto: user?.photoURL || "",
        title: caption || "Song Studio Project 🎵",
        type: "song_studio",
        caption: caption || "Check out my Song Studio project! 🎵",
        tracks,
        lyrics: selectedLyrics || "",
        mixVolumes,
        coverUrl: "",
        audioUrl: null,
        isVideo: false,
        likes: 0,
        comments: 0,
        plays: 0,
        createdAt: new Date().toISOString(),
        artistVerified: false,
      });

      setShared(true);
      setCaption("");
      setTimeout(() => setShared(false), 3000);
    } catch (e) {
      alert("Failed to share. Try again.");
    }
    setSharing(false);
  };

  return (
    <div>
      <textarea
        value={caption}
        onChange={e => setCaption(e.target.value)}
        placeholder="Write something about your track... (optional)"
        maxLength={300}
        style={{
          width: "100%", background: "#0a0a0a", border: "1px solid #222", borderRadius: 10,
          color: "#fff", fontSize: 13, padding: "10px 12px", resize: "none", height: 72,
          boxSizing: "border-box", marginBottom: 10, fontFamily: "inherit"
        }}
      />
      <button onClick={handleShare} disabled={sharing || shared} style={{
        width: "100%", padding: "12px", borderRadius: 10, border: "none",
        background: shared ? "#1db954" : "linear-gradient(135deg, #1db954, #17a345)",
        color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer"
      }}>
        {shared ? "✅ Shared to Feed!" : sharing ? "Sharing..." : "🚀 Share to Feed"}
      </button>
    </div>
  );
}
function ExportProjectButton({ selectedBeat, selectedMelody, selectedGuitar, selectedRecording, selectedLyrics, mixVolumes }) {
  const handleExport = () => {
    const project = {
      name: "MyMusic Project",
      exportedAt: new Date().toISOString(),
      tracks: {
        beat: selectedBeat ? { name: selectedBeat.name || "Beat", volume: mixVolumes.beatVolume } : null,
        piano: selectedMelody ? { name: selectedMelody.name || "Piano", volume: mixVolumes.pianoVolume } : null,
        guitar: selectedGuitar ? { name: selectedGuitar.name || "Guitar", chords: selectedGuitar.chords, volume: mixVolumes.guitarVolume } : null,
        voice: selectedRecording ? { name: selectedRecording.name || "Voice", volume: mixVolumes.voiceVolume } : null,
        lyrics: selectedLyrics || null,
      },
      mixVolumes,
    };
    const blob = new Blob([JSON.stringify(project, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mymusic-project-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <button onClick={handleExport} style={{
      width: "100%", padding: "12px", borderRadius: 10, border: "none",
      background: "linear-gradient(135deg, #2563eb, #1d4ed8)", color: "#fff", fontSize: 13, fontWeight: 700,
      cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 10,
      boxShadow: "0 4px 14px rgba(37,99,235,0.3)"
    }}>
      ⬇️ Export Project File
    </button>
  );
}
function TrackSelector({ icon, label, color, items, selected, onSelect, emptyMsg, renderItem, isPlaying }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ background: "#111", borderRadius: 14, padding: 16, border: `1px solid ${selected ? color : "#1a1a1a"}`, marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: open || selected ? 12 : 0 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: `${color}22`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>{icon}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 14, fontWeight: 800 }}>{label}</div>
          {selected && <div style={{ fontSize: 11, color: color, marginTop: 2 }}>✓ {renderItem(selected)}</div>}
        </div>
        {selected && (
          <button onClick={() => onSelect(null)} style={{ background: "none", border: "none", color: "#444", cursor: "pointer", fontSize: 16 }}>✕</button>
        )}
        <button onClick={() => setOpen(!open)} style={{ background: "#1a1a1a", border: "none", color: "#888", cursor: "pointer", padding: "6px 12px", borderRadius: 8, fontSize: 12 }}>
          {open ? "Hide" : "Choose"}
        </button>
      </div>
      {open && (
        <div>
          {items.length === 0 ? (
            <div style={{ fontSize: 12, color: "#444", textAlign: "center", padding: "8px 0" }}>{emptyMsg}</div>
          ) : items.map(item => (
            <div key={item.id} onClick={() => { onSelect(item); setOpen(false); }}
              style={{ padding: "10px 14px", borderRadius: 10, border: `1px solid ${selected?.id === item.id ? color : "#2a2a2a"}`, background: selected?.id === item.id ? `${color}15` : "#0a0a0a", cursor: "pointer", marginBottom: 6, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 13, color: selected?.id === item.id ? color : "#ccc" }}>{renderItem(item)}</span>
              {selected?.id === item.id && <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "none", stroke: color, strokeWidth: 2.5 }}><polyline points="20 6 9 17 4 12"/></svg>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── VIRTUAL GUITAR ───────────────────────────────────────────────────────────
function VirtualGuitar({ onBack }) {
  const audioCtxRef = useRef(null);
  const [activeChord, setActiveChord] = useState(null);
  const [recording, setRecording] = useState(false);
  const [recordedChords, setRecordedChords] = useState([]);
  const [savedGuitar, setSavedGuitar] = useState(() => JSON.parse(localStorage.getItem("saved_guitar") || "[]"));
  const [guitarName, setGuitarName] = useState("");
  const recordingRef = useRef(false);
  const recordedRef = useRef([]);
  const startTimeRef = useRef(0);

  const chords = [
    { name: "C", color: "#1db954", freqs: [261.63, 329.63, 392.00, 523.25, 659.25] },
    { name: "G", color: "#4776e6", freqs: [196.00, 246.94, 293.66, 392.00, 493.88] },
    { name: "Am", color: "#e91429", freqs: [220.00, 261.63, 329.63, 440.00, 523.25] },
    { name: "F", color: "#f59e0b", freqs: [174.61, 220.00, 261.63, 349.23, 440.00] },
    { name: "D", color: "#8b5cf6", freqs: [146.83, 185.00, 220.00, 293.66, 369.99] },
    { name: "Em", color: "#ec4899", freqs: [164.81, 196.00, 246.94, 329.63, 392.00] },
    { name: "A", color: "#06b6d4", freqs: [110.00, 138.59, 164.81, 220.00, 277.18] },
    { name: "E", color: "#84cc16", freqs: [82.41, 110.00, 164.81, 246.94, 329.63] },
    { name: "Dm", color: "#f97316", freqs: [146.83, 174.61, 220.00, 293.66, 349.23] },
    { name: "Bm", color: "#14b8a6", freqs: [123.47, 146.83, 185.00, 246.94, 293.66] },
    { name: "C7", color: "#6d28d9", freqs: [261.63, 311.13, 392.00, 466.16, 523.25] },
    { name: "G7", color: "#be185d", freqs: [196.00, 246.94, 293.66, 349.23, 440.00] },
  ];

  function getCtx() {
    if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    return audioCtxRef.current;
  }

  function strumChord(chord) {
    const ctx = getCtx();
    setActiveChord(chord.name);
    setTimeout(() => setActiveChord(null), 500);

    // Strum strings with slight delay between each string
    chord.freqs.forEach((freq, i) => {
      setTimeout(() => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        const distortion = ctx.createWaveShaper();
        
        // Create guitar-like distortion
        const curve = new Float32Array(256);
        for (let j = 0; j < 256; j++) {
          const x = (j * 2) / 256 - 1;
          curve[j] = (Math.PI + 200) * x / (Math.PI + 200 * Math.abs(x));
        }
        distortion.curve = curve;
        
        osc.connect(distortion);
        distortion.connect(gain);
        gain.connect(ctx.destination);
        
        osc.type = "sawtooth";
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.3, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.8);
        osc.start();
        osc.stop(ctx.currentTime + 0.8);
      }, i * 30);
    });

    if (recordingRef.current) {
      recordedRef.current.push({ chord: chord.name, time: Date.now() - startTimeRef.current });
    }
  }

  function toggleRec() {
    if (recording) {
      setRecording(false);
      recordingRef.current = false;
      setRecordedChords([...recordedRef.current]);
    } else {
      setRecording(true);
      recordingRef.current = true;
      recordedRef.current = [];
      startTimeRef.current = Date.now();
      setRecordedChords([]);
    }
  }

  function playBack() {
    recordedChords.forEach(({ chord, time }) => {
      setTimeout(() => {
        const c = chords.find(ch => ch.name === chord);
        if (c) strumChord(c);
      }, time);
    });
  }

  return (
    <div style={{ background: "#000", minHeight: "100vh", paddingBottom: 100, color: "#fff", fontFamily: "'DM Sans',sans-serif" }}>
      <BackButton onBack={onBack} title="Virtual Guitar 🎸" />
      <div style={{ padding: 16 }}>

        {/* Controls */}
        <div style={{ background: "#111", borderRadius: 14, padding: 14, marginBottom: 16, border: "1px solid #1a1a1a", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <button onClick={toggleRec} style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: recording ? "#e91429" : "#333", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
            {recording ? "⏹ Stop" : "⏺ Record"}
          </button>
          {recordedChords.length > 0 && (
            <button onClick={playBack} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #1db954", background: "transparent", color: "#1db954", fontSize: 13, cursor: "pointer" }}>▶ Play Back</button>
          )}
          {recording && <span style={{ color: "#e91429", fontSize: 12 }}>● Recording... {recordedChords.length} chords</span>}
          <span style={{ fontSize: 12, color: "#555", marginLeft: "auto" }}>Tap chords to strum</span>
        </div>

        {/* Guitar neck visual */}
        <div style={{ background: "#111", borderRadius: 14, padding: 16, marginBottom: 16, border: "1px solid #1a1a1a" }}>
          <div style={{ fontSize: 12, color: "#555", marginBottom: 12, textAlign: "center" }}>🎸 Guitar Strings</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {["E2", "A2", "D3", "G3", "B3", "E4"].map((string, i) => (
              <div key={string} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 10, color: "#555", minWidth: 24 }}>{string}</span>
                <div style={{ flex: 1, height: i < 2 ? 3 : i < 4 ? 2 : 1, background: activeChord ? "#1db954" : "#333", borderRadius: 1, transition: "background 0.1s", boxShadow: activeChord ? "0 0 8px rgba(29,185,84,0.6)" : "none" }} />
              </div>
            ))}
          </div>
        </div>

        {/* Chord buttons */}
        <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 14, fontWeight: 800, marginBottom: 12 }}>Chords</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
          {chords.map(chord => (
            <button key={chord.name} onClick={() => strumChord(chord)}
              style={{ padding: "20px 8px", borderRadius: 12, border: `2px solid ${activeChord === chord.name ? chord.color : "#1a1a1a"}`, background: activeChord === chord.name ? chord.color : "#111", color: activeChord === chord.name ? "#000" : "#fff", fontSize: 16, fontWeight: 800, cursor: "pointer", transition: "all 0.1s", transform: activeChord === chord.name ? "scale(0.95)" : "scale(1)", fontFamily: "'Syne',sans-serif" }}>
              {chord.name}
            </button>
          ))}
        </div>

        {/* Save section */}
        <div style={{ background: "#111", borderRadius: 14, padding: 16, border: "1px solid #1a1a1a", marginBottom: 10 }}>
          <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 14, fontWeight: 800, marginBottom: 12 }}>Save Recording</div>
          <div style={{ display: "flex", gap: 10 }}>
            <input value={guitarName} onChange={e => setGuitarName(e.target.value)} placeholder="Name your recording..."
              style={{ flex: 1, padding: "10px 13px", borderRadius: 10, border: "1px solid #2a2a2a", background: "#0a0a0a", color: "#fff", fontFamily: "'DM Sans',sans-serif", fontSize: 13, outline: "none" }} />
            <button onClick={() => {
              if (recordedChords.length === 0) { alert("Record something first!"); return; }
              const name = guitarName.trim() || `Guitar ${savedGuitar.length + 1}`;
              const rec = { id: Date.now(), name, chords: recordedChords, date: new Date().toLocaleDateString() };
              const updated = [rec, ...savedGuitar];
              setSavedGuitar(updated);
              localStorage.setItem("saved_guitar", JSON.stringify(updated));
              setGuitarName("");
            }} style={{ padding: "10px 20px", borderRadius: 10, border: "none", background: "#f59e0b", color: "#000", fontSize: 13, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>Save</button>
          </div>
        </div>

        {savedGuitar.length > 0 && (
          <div style={{ background: "#111", borderRadius: 14, padding: 16, border: "1px solid #1a1a1a" }}>
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 14, fontWeight: 800, marginBottom: 12 }}>My Guitar Recordings ({savedGuitar.length})</div>
            {savedGuitar.map((rec, i) => (
              <div key={rec.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: i < savedGuitar.length - 1 ? "1px solid #0a0a0a" : "none" }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(245,158,11,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>🎸</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{rec.name}</div>
                  <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>{rec.date} · {rec.chords.length} chords</div>
                </div>
                <button onClick={() => {
                  rec.chords.forEach(({ chord, time }) => {
                    setTimeout(() => {
                      const c = chords.find(ch => ch.name === chord);
                      if (c) strumChord(c);
                    }, time);
                  });
                }} style={{ padding: "7px 14px", borderRadius: 8, border: "none", background: "#f59e0b", color: "#000", fontSize: 12, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>▶ Play</button>
                <button onClick={() => { const updated = savedGuitar.filter(r => r.id !== rec.id); setSavedGuitar(updated); localStorage.setItem("saved_guitar", JSON.stringify(updated)); }}
                  style={{ background: "none", border: "none", color: "#444", cursor: "pointer", fontSize: 16, flexShrink: 0 }}>🗑️</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
// ─── BACK BUTTON ──────────────────────────────────────────────────────────────
function BackButton({ onBack, title }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: "rgba(0,0,0,0.9)", position: "sticky", top: 56, zIndex: 10, borderBottom: "1px solid #1a1a1a", backdropFilter: "blur(20px)" }}>
      <button onClick={onBack} style={{ background: "#111", border: "1px solid #2a2a2a", color: "#fff", width: 34, height: 34, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
        <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "none", stroke: "#fff", strokeWidth: 2 }}><polyline points="15 18 9 12 15 6"/></svg>
      </button>
      <span style={{ fontFamily: "'Syne',sans-serif", fontSize: 17, fontWeight: 800 }}>{title}</span>
    </div>
  );
}

// ─── BEAT MAKER ──────────────────────────────────────────────────────────────
function BeatMaker({ onBack }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [bpm, setBpm] = useState(120);
  const [currentStep, setCurrentStep] = useState(0);
  const [pattern, setPattern] = useState({
    kick:  [1,0,0,0, 1,0,0,0, 1,0,0,0, 1,0,0,0],
    snare: [0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,0],
    hihat: [1,0,1,0, 1,0,1,0, 1,0,1,0, 1,0,1,0],
    clap:  [0,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,0,0],
    bass:  [0,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,0,0],
    tom:   [0,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,0,0],
  });
  const [savedBeats, setSavedBeats] = useState(() => JSON.parse(localStorage.getItem("saved_beats") || "[]"));
  const [beatName, setBeatName] = useState("");
  const intervalRef = useRef(null);
  const audioCtxRef = useRef(null);
  const stepRef = useRef(0);
  const patternRef = useRef(pattern);
  useEffect(() => { return () => clearInterval(intervalRef.current); }, []);
  const trackColors = { kick: "#1db954", snare: "#ff6b35", hihat: "#4776e6", clap: "#8d67ab", bass: "#e13300", tom: "#148a08" };
  const trackLabels = { kick: "KICK", snare: "SNARE", hihat: "HI-HAT", clap: "CLAP", bass: "BASS", tom: "TOM" };
  patternRef.current = pattern;

  function getCtx() {
    if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    return audioCtxRef.current;
  }

  function playSound(type) {
    const ctx = getCtx();
    const gain = ctx.createGain();
    gain.connect(ctx.destination);
    if (type === "kick") {
      const osc = ctx.createOscillator();
      osc.connect(gain);
      osc.frequency.setValueAtTime(150, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      gain.gain.setValueAtTime(1, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      osc.type = "sine"; osc.start(); osc.stop(ctx.currentTime + 0.5);
    } else if (type === "snare" || type === "clap" || type === "hihat") {
      const buf = ctx.createBuffer(1, ctx.sampleRate * (type === "hihat" ? 0.05 : 0.2), ctx.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
      const noise = ctx.createBufferSource();
      noise.buffer = buf;
      if (type === "hihat") {
        const filter = ctx.createBiquadFilter();
        filter.type = "highpass"; filter.frequency.value = 8000;
        noise.connect(filter); filter.connect(gain);
      } else { noise.connect(gain); }
      gain.gain.setValueAtTime(type === "hihat" ? 0.3 : 0.7, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
      noise.start(); noise.stop(ctx.currentTime + 0.2);
    } else {
      const osc = ctx.createOscillator();
      osc.connect(gain);
      osc.frequency.setValueAtTime(type === "bass" ? 80 : 200, ctx.currentTime);
      if (type === "tom") osc.frequency.exponentialRampToValueAtTime(50, ctx.currentTime + 0.3);
      gain.gain.setValueAtTime(0.8, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
      osc.type = type === "bass" ? "sawtooth" : "sine"; osc.start(); osc.stop(ctx.currentTime + 0.3);
    }
  }

  function togglePlay() {
    if (isPlaying) {
      clearInterval(intervalRef.current); setIsPlaying(false); setCurrentStep(0); stepRef.current = 0;
    } else {
      setIsPlaying(true);
      intervalRef.current = setInterval(() => {
        const step = stepRef.current;
        setCurrentStep(step);
        Object.keys(patternRef.current).forEach(track => { if (patternRef.current[track][step]) playSound(track); });
        stepRef.current = (step + 1) % 16;
      }, (60 / bpm / 4) * 1000);
    }
  }

  return (
    <div style={{ background: "#000", minHeight: "100vh", paddingBottom: 100 }}>
      <BackButton onBack={() => { clearInterval(intervalRef.current); setIsPlaying(false); onBack(); }} title="Beat Maker 🥁" />
      <div style={{ padding: 16 }}>
        <div style={{ background: "#111", borderRadius: 16, padding: 16, marginBottom: 12, border: "1px solid #1a1a1a" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <button onClick={togglePlay} style={{ width: 52, height: 52, borderRadius: "50%", border: "none", background: isPlaying ? "#e91429" : "#1db954", color: "#000", fontSize: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {isPlaying ? <svg viewBox="0 0 24 24" style={{ width: 22, height: 22, fill: "#000" }}><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg> : <svg viewBox="0 0 24 24" style={{ width: 22, height: 22, fill: "#000", marginLeft: 3 }}><polygon points="5 3 19 12 5 21 5 3"/></svg>}
            </button>
            <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 12, color: "#555" }}>BPM</span>
              <span style={{ fontFamily: "'Syne',sans-serif", fontSize: 18, fontWeight: 800, color: "#1db954", minWidth: 40 }}>{bpm}</span>
              <input type="range" min="60" max="200" value={bpm} onChange={e => setBpm(Number(e.target.value))} style={{ flex: 1, accentColor: "#1db954" }} />
            </div>
            <button onClick={() => setPattern({ kick: Array(16).fill(0), snare: Array(16).fill(0), hihat: Array(16).fill(0), clap: Array(16).fill(0), bass: Array(16).fill(0), tom: Array(16).fill(0) })} style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #333", background: "transparent", color: "#666", fontSize: 12, cursor: "pointer" }}>Clear</button>
          </div>
          <div style={{ display: "flex", gap: 4, marginBottom: 12 }}>
            {Array(16).fill(0).map((_, i) => <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: currentStep === i && isPlaying ? "#1db954" : "#1a1a1a" }} />)}
          </div>
          {Object.keys(pattern).map(track => (
            <div key={track} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ width: 48, fontSize: 9, fontWeight: 700, color: trackColors[track], letterSpacing: "0.05em" }}>{trackLabels[track]}</span>
              <div style={{ display: "flex", gap: 3, flex: 1 }}>
                {pattern[track].map((active, step) => (
                  <button key={step} onClick={() => setPattern(prev => ({ ...prev, [track]: prev[track].map((v, i) => i === step ? (v ? 0 : 1) : v) }))}
                    style={{ flex: 1, height: 32, borderRadius: 5, border: "none", background: active ? trackColors[track] : "#1a1a1a", opacity: active ? 1 : 0.4, outline: currentStep === step && isPlaying ? "2px solid rgba(255,255,255,0.3)" : "none", cursor: "pointer" }} />
                ))}
              </div>
            </div>
          ))}
        </div>

        <div style={{ background: "#111", borderRadius: 14, padding: 16, border: "1px solid #1a1a1a", marginBottom: 10 }}>
          <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 14, fontWeight: 800, marginBottom: 12 }}>Save Beat</div>
          <div style={{ display: "flex", gap: 10 }}>
            <input value={beatName} onChange={e => setBeatName(e.target.value)} placeholder="Name your beat..." onKeyDown={e => e.key === "Enter" && document.getElementById("save-beat-btn").click()}
              style={{ flex: 1, padding: "10px 13px", borderRadius: 10, border: "1px solid #2a2a2a", background: "#0a0a0a", color: "#fff", fontFamily: "'DM Sans',sans-serif", fontSize: 13, outline: "none" }} />
            <button id="save-beat-btn" onClick={() => {
              const name = beatName.trim() || `Beat ${savedBeats.length + 1}`;
              const beat = { id: Date.now(), name, bpm, pattern, date: new Date().toLocaleDateString() };
              const updated = [beat, ...savedBeats];
              setSavedBeats(updated);
              localStorage.setItem("saved_beats", JSON.stringify(updated));
              setBeatName("");
            }} style={{ padding: "10px 20px", borderRadius: 10, border: "none", background: "#1db954", color: "#000", fontSize: 13, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>Save</button>
          </div>
        </div>

        {savedBeats.length > 0 && (
          <div style={{ background: "#111", borderRadius: 14, padding: 16, border: "1px solid #1a1a1a" }}>
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 14, fontWeight: 800, marginBottom: 12 }}>My Beats ({savedBeats.length})</div>
            {savedBeats.map((beat, i) => (
              <div key={beat.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: i < savedBeats.length - 1 ? "1px solid #0a0a0a" : "none" }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: "linear-gradient(135deg, #1db954, #0a8f3c)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>🥁</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{beat.name}</div>
                  <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>{beat.date} · {beat.bpm} BPM</div>
                </div>
                <button onClick={() => { setPattern(beat.pattern); setBpm(beat.bpm); }} style={{ padding: "7px 14px", borderRadius: 8, border: "none", background: "#1db954", color: "#000", fontSize: 12, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>Load</button>
                <button onClick={() => { const updated = savedBeats.filter(b => b.id !== beat.id); setSavedBeats(updated); localStorage.setItem("saved_beats", JSON.stringify(updated)); }} style={{ background: "none", border: "none", color: "#444", cursor: "pointer", fontSize: 16, flexShrink: 0 }}>🗑️</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── VIRTUAL PIANO ────────────────────────────────────────────────────────────
function VirtualPiano({ onBack }) {
  useEffect(() => {
    if (window.screen.orientation && window.screen.orientation.lock) {
      window.screen.orientation.lock("landscape").catch(() => {});
    }
    return () => {
      if (window.screen.orientation && window.screen.orientation.unlock) {
        window.screen.orientation.unlock();
      }
    };
  }, []);
  const audioCtxRef = useRef(null);
  const [activeKeys, setActiveKeys] = useState(new Set());
  const [octave, setOctave] = useState(4);
  const [recording, setRecording] = useState(false);
  const [recordedNotes, setRecordedNotes] = useState([]);
  const [savedMelodies, setSavedMelodies] = useState(() => JSON.parse(localStorage.getItem("saved_melodies") || "[]"));
  const [melodyName, setMelodyName] = useState("");
  const recordingRef = useRef(false);
  const recordedRef = useRef([]);
  const startTimeRef = useRef(0);

  function getCtx() {
    if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    return audioCtxRef.current;
  }

  function noteToFreq(note, oct) {
    const notes = { C: 0, "C#": 1, D: 2, "D#": 3, E: 4, F: 5, "F#": 6, G: 7, "G#": 8, A: 9, "A#": 10, B: 11 };
    return 440 * Math.pow(2, (notes[note] + (oct + 1) * 12 - 69) / 12);
  }

  function playNote(note, oct) {
    const ctx = getCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.type = "triangle"; osc.frequency.value = noteToFreq(note, oct);
    gain.gain.setValueAtTime(0.5, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 1.5);
    osc.start(); osc.stop(ctx.currentTime + 1.5);
    setActiveKeys(prev => new Set([...prev, `${note}${oct}`]));
    setTimeout(() => setActiveKeys(prev => { const n = new Set(prev); n.delete(`${note}${oct}`); return n; }), 300);
    if (recordingRef.current) recordedRef.current.push({ note, oct, time: Date.now() - startTimeRef.current });
  }

  function toggleRec() {
    if (recording) { setRecording(false); recordingRef.current = false; setRecordedNotes([...recordedRef.current]); }
    else { setRecording(true); recordingRef.current = true; recordedRef.current = []; startTimeRef.current = Date.now(); setRecordedNotes([]); }
  }

  const whiteKeys = ["C", "D", "E", "F", "G", "A", "B", "C2"];
  const hasBlack = { C: true, D: true, F: true, G: true, A: true };

  return (
    <div style={{ background: "#000", minHeight: "100vh", paddingBottom: 100 }}>
      <BackButton onBack={onBack} title="Virtual Piano 🎹" />
      <div style={{ padding: 16 }}>
        <div style={{ background: "#111", borderRadius: 16, padding: 16, marginBottom: 12, border: "1px solid #1a1a1a" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button onClick={() => setOctave(Math.max(1, octave - 1))} style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid #333", background: "transparent", color: "#fff", cursor: "pointer" }}>−</button>
              <span style={{ fontFamily: "'Syne',sans-serif", fontSize: 16, fontWeight: 800, color: "#1db954", minWidth: 28, textAlign: "center" }}>C{octave}</span>
              <button onClick={() => setOctave(Math.min(7, octave + 1))} style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid #333", background: "transparent", color: "#fff", cursor: "pointer" }}>+</button>
            </div>
            <button onClick={toggleRec} style={{ padding: "7px 14px", borderRadius: 8, border: "none", background: recording ? "#e91429" : "#333", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
              {recording ? "⏹ Stop" : "⏺ Record"}
            </button>
            {recordedNotes.length > 0 && (
              <button onClick={() => recordedNotes.forEach(({ note, oct, time }) => setTimeout(() => playNote(note, oct), time))}
                style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid #1db954", background: "transparent", color: "#1db954", fontSize: 12, cursor: "pointer" }}>▶ Play Back</button>
            )}
            {recording && <span style={{ color: "#e91429", fontSize: 12 }}>● Recording...</span>}
          </div>
          <div style={{ fontSize: 11, color: "#555", marginBottom: 8, textAlign: "center" }}>
 <div style={{ background: "#111", borderRadius: 10, padding: "8px 14px", marginBottom: 12, display: "flex", alignItems: "center", gap: 8, border: "1px solid #2a2a2a" }}>
  <span style={{ fontSize: 14 }}>🔄</span>
  <span style={{ fontSize: 12, color: "#555" }}>Rotate phone to landscape for more keys</span>
</div>
</div>
<div style={{ display: "flex", gap: 2, height: 160, overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            {whiteKeys.map(note => (
              <div key={note} style={{ position: "relative", flexShrink: 0 }}>
                <div onMouseDown={() => playNote(note === "C2" ? "C" : note, note === "C2" ? octave + 1 : octave)} onTouchStart={e => { e.preventDefault(); playNote(note === "C2" ? "C" : note, note === "C2" ? octave + 1 : octave); }}
                  style={{width: 52, height: 140, background: activeKeys.has(`${note}${octave}`) ? "#b3ffcc" : "#fff", border: "1px solid #ccc", borderRadius: "0 0 6px 6px", cursor: "pointer", display: "flex", alignItems: "flex-end", justifyContent: "center", paddingBottom: 6 }}>
                 <span style={{ fontSize: 9, color: "#999", fontWeight: 700 }}>{note === "C2" ? `C${octave+1}` : `${note}${octave}`}</span>
                </div>
                {hasBlack[note] && note !== "C2" && (
                  <div onMouseDown={() => playNote(`${note}#`, octave)} onTouchStart={e => { e.preventDefault(); playNote(`${note}#`, octave); }}
                    style={{width: 34, height: 90, background: activeKeys.has(`${note}#${octave}`) ? "#1db954" : "#111", borderRadius: "0 0 4px 4px", position: "absolute", top: 0, right: -17, zIndex: 2, cursor: "pointer", border: "1px solid #000" }} />
                )}
              </div>
          ))}
          </div>
        </div>

        <div style={{ background: "#111", borderRadius: 14, padding: 16, border: "1px solid #1a1a1a", marginBottom: 10 }}>
          <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 14, fontWeight: 800, marginBottom: 12 }}>Save Melody</div>
          <div style={{ display: "flex", gap: 10 }}>
            <input value={melodyName} onChange={e => setMelodyName(e.target.value)} placeholder="Name your melody..."
              style={{ flex: 1, padding: "10px 13px", borderRadius: 10, border: "1px solid #2a2a2a", background: "#0a0a0a", color: "#fff", fontFamily: "'DM Sans',sans-serif", fontSize: 13, outline: "none" }} />
            <button onClick={() => {
              if (recordedNotes.length === 0) { alert("Record something first!"); return; }
              const name = melodyName.trim() || `Melody ${savedMelodies.length + 1}`;
              const melody = { id: Date.now(), name, notes: recordedNotes, date: new Date().toLocaleDateString() };
              const updated = [melody, ...savedMelodies];
              setSavedMelodies(updated);
              localStorage.setItem("saved_melodies", JSON.stringify(updated));
              setMelodyName("");
            }} style={{ padding: "10px 20px", borderRadius: 10, border: "none", background: "#1db954", color: "#000", fontSize: 13, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>Save</button>
          </div>
        </div>

        {savedMelodies.length > 0 && (
          <div style={{ background: "#111", borderRadius: 14, padding: 16, border: "1px solid #1a1a1a" }}>
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 14, fontWeight: 800, marginBottom: 12 }}>My Melodies ({savedMelodies.length})</div>
            {savedMelodies.map((melody, i) => (
              <div key={melody.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: i < savedMelodies.length - 1 ? "1px solid #0a0a0a" : "none" }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(71,118,230,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>🎹</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{melody.name}</div>
                  <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>{melody.date} · {melody.notes.length} notes</div>
                </div>
                <button onClick={() => { setRecordedNotes(melody.notes); melody.notes.forEach(({ note, oct, time }) => setTimeout(() => playNote(note, oct), time)); }}
                  style={{ padding: "7px 14px", borderRadius: 8, border: "none", background: "#4776e6", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>▶ Play</button>
                <button onClick={() => { const updated = savedMelodies.filter(m => m.id !== melody.id); setSavedMelodies(updated); localStorage.setItem("saved_melodies", JSON.stringify(updated)); }}
                  style={{ background: "none", border: "none", color: "#444", cursor: "pointer", fontSize: 16, flexShrink: 0 }}>🗑️</button>
              </div>
            ))}
                   </div>
        )}
      </div>
    </div>
  );
}

// ─── VOICE RECORDER ──────────────────────────────────────────────────────────
function VoiceRecorder({ onBack }) {
  const [recording, setRecording] = useState(false);
  const [recordings, setRecordings] = useState(() => JSON.parse(localStorage.getItem("saved_recordings") || "[]").filter(r => r.url));
  const [timer, setTimer] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = e => chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const url = URL.createObjectURL(blob);
        const newRec = { id: Date.now(), url, name: `Recording ${recordings.length + 1}`, duration: timer, date: new Date().toLocaleDateString() };
        setRecordings(prev => {
          const updated = [newRec, ...prev];
          return updated;
        });
        stream.getTracks().forEach(t => t.stop());
      };
      mr.start(); setRecording(true); setTimer(0);
      timerRef.current = setInterval(() => setTimer(t => t + 1), 1000);
    } catch { alert("Microphone access denied!"); }
  }

  function stopRecording() { mediaRecorderRef.current?.stop(); clearInterval(timerRef.current); setRecording(false); }
  function fmt(s) { return `${Math.floor(s / 60)}:${(s % 60 < 10 ? "0" : "") + (s % 60)}`; }

  return (
    <div style={{ background: "#000", minHeight: "100vh", paddingBottom: 100 }}>
      <BackButton onBack={onBack} title="Voice Recorder 🎙️" />
      <div style={{ padding: 16 }}>
        <div style={{ background: "#111", borderRadius: 16, padding: 24, marginBottom: 16, textAlign: "center", border: "1px solid #1a1a1a" }}>
          <button onClick={recording ? stopRecording : startRecording}
            style={{ width: 96, height: 96, borderRadius: "50%", border: "none", background: recording ? "#e91429" : "#1db954", color: "#000", fontSize: 34, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", boxShadow: recording ? "0 0 30px rgba(233,20,41,0.4)" : "0 0 30px rgba(29,185,84,0.3)" }}>
            {recording ? "⏹" : "🎙️"}
          </button>
          <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 30, fontWeight: 800, color: recording ? "#e91429" : "#fff", marginBottom: 6 }}>{fmt(timer)}</div>
          <div style={{ fontSize: 13, color: "#555" }}>{recording ? "Recording... tap to stop" : "Tap to start recording"}</div>
        </div>
        {recordings.map(rec => (
          <div key={rec.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: 14, background: "#111", borderRadius: 12, marginBottom: 8, border: "1px solid #1a1a1a" }}>
            <div style={{ width: 44, height: 44, borderRadius: 10, background: "rgba(29,185,84,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>🎙️</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{rec.name}</div>
              <div style={{ fontSize: 11, color: "#555" }}>{rec.date} · {fmt(rec.duration)}</div>
              <audio src={rec.url} controls style={{ width: "100%", marginTop: 8, height: 32, accentColor: "#1db954" }} />
            </div>
            <button onClick={() => setRecordings(prev => prev.filter(r => r.id !== rec.id))} style={{ background: "none", border: "none", color: "#444", fontSize: 16, cursor: "pointer" }}>🗑️</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── LYRICS WRITER ────────────────────────────────────────────────────────────
function LyricsWriter({ uid, onBack }) {
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [content, setContent] = useState("");
  const [saved, setSaved] = useState(() => { try { return JSON.parse(localStorage.getItem(`my_lyrics_${uid}`) || "[]"); } catch { return []; } });
  const [viewing, setViewing] = useState(null);
  const [success, setSuccess] = useState("");

  function save() {
    if (!title || !content) { alert("Please enter title and lyrics!"); return; }
    const item = { id: Date.now(), title, artist, content, date: new Date().toLocaleDateString() };
    const updated = [item, ...saved];
    setSaved(updated); localStorage.setItem(`my_lyrics_${uid}`, JSON.stringify(updated));
    setTitle(""); setArtist(""); setContent("");
    setSuccess("✅ Saved!"); setTimeout(() => setSuccess(""), 3000);
  }

  if (viewing) return (
    <div style={{ background: "#000", minHeight: "100vh", paddingBottom: 100 }}>
      <BackButton onBack={() => setViewing(null)} title={viewing.title} />
      <div style={{ padding: 16 }}>
        <div style={{ background: "#111", borderRadius: 16, padding: 20, border: "1px solid #1a1a1a" }}>
          {viewing.artist && <p style={{ color: "#1db954", fontSize: 13, marginBottom: 16 }}>{viewing.artist}</p>}
          <p style={{ color: "#e0e0e0", fontSize: 15, lineHeight: 2, whiteSpace: "pre-wrap" }}>{viewing.content}</p>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ background: "#000", minHeight: "100vh", paddingBottom: 100 }}>
      <BackButton onBack={onBack} title="Lyrics Writer ✍️" />
      <div style={{ padding: 16 }}>
        {success && <div style={{ background: "rgba(29,185,84,0.1)", border: "1px solid rgba(29,185,84,0.3)", color: "#1db954", padding: 12, borderRadius: 10, fontSize: 13, marginBottom: 14, textAlign: "center" }}>{success}</div>}
        <div style={{ background: "#111", borderRadius: 16, padding: 16, marginBottom: 16, border: "1px solid #1a1a1a" }}>
          <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 15, fontWeight: 800, marginBottom: 14 }}>Write New Lyrics</div>
          {[{ ph: "Song title...", val: title, set: setTitle }, { ph: "Artist name...", val: artist, set: setArtist }].map((f, i) => (
            <input key={i} placeholder={f.ph} value={f.val} onChange={e => f.set(e.target.value)} style={{ width: "100%", padding: "11px 13px", borderRadius: 10, border: "1px solid #2a2a2a", background: "#0a0a0a", color: "#fff", fontFamily: "'DM Sans',sans-serif", fontSize: 14, outline: "none", marginBottom: 10 }} />
          ))}
          <textarea placeholder={"[Verse 1]\nYour lyrics here...\n\n[Chorus]\nYour chorus here..."} rows={8} value={content} onChange={e => setContent(e.target.value)}
            style={{ width: "100%", padding: "11px 13px", borderRadius: 10, border: "1px solid #2a2a2a", background: "#0a0a0a", color: "#fff", fontFamily: "'DM Sans',sans-serif", fontSize: 14, outline: "none", resize: "vertical", marginBottom: 12, lineHeight: 1.8 }} />
          <button onClick={save} style={{ width: "100%", padding: 13, borderRadius: 10, border: "none", background: "#1db954", color: "#000", fontFamily: "'Syne',sans-serif", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>💾 Save Lyrics</button>
        </div>
        {saved.length > 0 && (
          <div>
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 15, fontWeight: 800, marginBottom: 12 }}>Saved Lyrics ({saved.length})</div>
            {saved.map(lyric => (
              <div key={lyric.id} onClick={() => setViewing(lyric)} style={{ display: "flex", alignItems: "center", gap: 12, padding: 14, background: "#111", borderRadius: 12, marginBottom: 8, border: "1px solid #1a1a1a", cursor: "pointer" }}>
                <div style={{ width: 44, height: 44, borderRadius: 10, background: "rgba(139,92,246,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>✍️</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{lyric.title}</div>
                  <div style={{ fontSize: 11, color: "#555" }}>{lyric.artist || "Unknown"} · {lyric.date}</div>
                </div>
                <button onClick={e => { e.stopPropagation(); const u = saved.filter(l => l.id !== lyric.id); setSaved(u); localStorage.setItem(`my_lyrics_${uid}`, JSON.stringify(u)); }} style={{ background: "none", border: "none", color: "#444", fontSize: 16, cursor: "pointer" }}>🗑️</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}